import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { toast } from '../../../../../ui/toast';
import { Button } from '../../../../../ui';
import { Modal } from '../../../../../ui/modal/modal';
import {
  addOrganizationUnit,
  getOrganizationTree,
  updateOrganizationTree,
} from '../../../../../utils/api_service/organization_unit';
import commonStyles from '../../../admin.module.css';
import LoadingSpinner from '../../../../job_openings/create/ui/LoaderSpinner';
import { CreateUnitModal } from '../../Modals/CreateUnitModal';
import { DeleteUnitModal } from '../../Modals/DeleteUnitModal';
import { EditNameModal } from '../../Modals/EditNameModal';
import { OrganizationUnit, TreeStructure } from '../../TreeStructure';

export const OrganizationHierarchy = () => {
  const { t } = useTranslation(NS.adminPages);
  const [organizationTree, setOrganizationTree] = useState<OrganizationUnit>();
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedUnit, setSelectedUnit] =
    useState<OrganizationUnit | null>(null);
  const [targetUnit, setTargetUnit] = useState<OrganizationUnit | null>(null);
  const [editNameModal, setEditNameModal] = useState<boolean>(false);
  const [addUnitModal, setAddUnitModal] = useState<boolean>(false);
  const [deleteUnitModal, setDeleteUnitModal] = useState<boolean>(false);
  const [selectedOrganizationUnit, setSelectedOrganizationUnit] =
    useState<OrganizationUnit | undefined>();
  const [editedOrgUnits, setEditedOrgUnits] = useState<OrganizationUnit[]>();

  useEffect(() => {
    setLoading(true);
    getOrganizationTree()
      .then((response) => {
        setOrganizationTree(response);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const applyTreeChanges = () => {
    if (editedOrgUnits && editedOrgUnits.length > 0) {
      updateOrganizationTree(editedOrgUnits).then((response) => {
        if (response.id) {
          setEditedOrgUnits(undefined);
        }
      });
    }
  };

  const handleDragStart = (folder: OrganizationUnit) => {
    setSelectedUnit(folder);
  };

  const handleDragEnter = (folder: OrganizationUnit) => {
    setTargetUnit(folder);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>,
    folder: OrganizationUnit,
  ) => {
    event.preventDefault();
    if (
      selectedUnit &&
      targetUnit &&
      organizationTree &&
      selectedUnit.parentOrganizationUnitId.length !== 0 &&
      selectedUnit.parentOrganizationUnitId !== targetUnit.id &&
      selectedUnit.id !== targetUnit.id
    ) {
      const updatedFolderData = moveUnit(
        organizationTree,
        selectedUnit,
        targetUnit,
      );
      setOrganizationTree(updatedFolderData);
    }
    setSelectedUnit(null);
    setTargetUnit(null);
  };

  const editOrganizationUnit = (
    targetUnit: OrganizationUnit,
    updatedObject: Partial<OrganizationUnit>,
  ): void => {
    setEditedOrgUnits((prevEditedOrgUnits) => {
      if (!prevEditedOrgUnits) {
        return [{ ...targetUnit, ...updatedObject }];
      }

      const updatedUnits = prevEditedOrgUnits.map((unit) => {
        if (unit.id === targetUnit.id) {
          return { ...unit, ...updatedObject };
        }
        return unit;
      });

      if (!isOrganizationUnitIncluded(targetUnit, prevEditedOrgUnits)) {
        updatedUnits.push({ ...targetUnit, ...updatedObject });
      }

      return updatedUnits;
    });
  };

  const isOrganizationUnitIncluded = (
    targetUnit: OrganizationUnit,
    units: OrganizationUnit[],
  ): boolean => {
    return units.some((unit) => unit.id === targetUnit.id);
  };

  const onEditClick = (selectedOrgUnit: OrganizationUnit) => {
    setSelectedOrganizationUnit(selectedOrgUnit);
    setEditNameModal(true);
  };

  const onAddUnitClick = (selectedOrgUnit: OrganizationUnit) => {
    setSelectedOrganizationUnit(selectedOrgUnit);
    setAddUnitModal(true);
  };

  const onDeleteClick = (selectedOrgUnit: OrganizationUnit) => {
    setSelectedOrganizationUnit(selectedOrgUnit);
    setDeleteUnitModal(true);
  };

  const addNewUnit = (unitName: string) => {
    if (selectedOrganizationUnit) {
      addOrganizationUnit(unitName, selectedOrganizationUnit.id).then(
        (response) => {
          if (response.id) {
            let newUnit = response;
            newUnit = { ...newUnit, children: [] };
            setOrganizationTree((prevOrganizationTree) => {
              if (prevOrganizationTree) {
                if (
                  selectedOrganizationUnit.parentOrganizationUnitId.length !== 0
                ) {
                  return {
                    ...prevOrganizationTree,
                    children: addNewUnitToSelectedUnit(
                      prevOrganizationTree.children,
                      newUnit,
                    ),
                  };
                } else {
                  return {
                    ...prevOrganizationTree,
                    children: [...prevOrganizationTree.children, newUnit],
                  };
                }
              }
              return prevOrganizationTree;
            });
          } else {
            toast.error(response.errors, { position: 'bottom-center' });
          }
        },
      );
      setAddUnitModal(false);
    }
  };

  const deleteUnit = () => {
    if (selectedOrganizationUnit) {
      setOrganizationTree((organizationTree) => {
        if (organizationTree) {
          return {
            ...organizationTree,
            children: deleteOrganizationUnitById(
              organizationTree.children,
              selectedOrganizationUnit.id,
            ),
          };
        }
        return organizationTree;
      });
      editOrganizationUnit(selectedOrganizationUnit, {
        isDirty: true,
        isDeleted: true,
      });
      setDeleteUnitModal(false);
    }
  };

  const editName = (unitName: string) => {
    if (selectedOrganizationUnit) {
      setOrganizationTree((organizationTree) => {
        if (organizationTree) {
          return {
            ...organizationTree,
            children: editDataStructure(
              organizationTree.children,
              selectedOrganizationUnit.id,
              { unitName: unitName },
            ),
          };
        }
        return organizationTree;
      });
      if (
        editedOrgUnits &&
        isOrganizationUnitIncluded(selectedOrganizationUnit, editedOrgUnits)
      ) {
        editOrganizationUnit(selectedOrganizationUnit, { unitName: unitName });
      } else {
        editOrganizationUnit(selectedOrganizationUnit, {
          unitName: unitName,
          isDirty: true,
          isDeleted: false,
        });
      }
      setEditNameModal(false);
    }
  };

  const moveUnit = (
    currentUnitData: OrganizationUnit,
    selectedUnit: OrganizationUnit,
    targetUnit: OrganizationUnit,
  ): OrganizationUnit => {
    const updatedSelectedUnit = {
      ...selectedUnit,
      parentOrganizationUnitId: targetUnit.id,
    };

    if (
      editedOrgUnits &&
      isOrganizationUnitIncluded(selectedUnit, editedOrgUnits)
    ) {
      editOrganizationUnit(selectedUnit, {
        parentOrganizationUnitId: targetUnit.id,
      });
    } else {
      editOrganizationUnit(updatedSelectedUnit, {
        isDirty: true,
        isDeleted: false,
      });
    }

    if (currentUnitData.id === selectedUnit.id) {
      return {
        ...currentUnitData,
        children: currentUnitData.children?.filter(
          (child) => child.id !== selectedUnit.id,
        ),
      };
    }

    const updatedChildren = currentUnitData.children?.map((child) =>
      moveUnit(child, selectedUnit, targetUnit),
    );

    if (currentUnitData.id === targetUnit.id) {
      return {
        ...currentUnitData,
        children: [...(updatedChildren || []), updatedSelectedUnit],
      };
    }

    if (updatedChildren !== currentUnitData.children) {
      return {
        ...currentUnitData,
        children: updatedChildren?.filter(
          (child) => child.id !== selectedUnit.id,
        ),
      };
    }

    return currentUnitData;
  };

  const addNewUnitToSelectedUnit = (
    units: OrganizationUnit[],
    newUnit: OrganizationUnit,
  ): OrganizationUnit[] => {
    return units.map((unit) => {
      if (selectedOrganizationUnit && unit.id === selectedOrganizationUnit.id) {
        return {
          ...unit,
          children: [...unit.children, newUnit],
        };
      } else if (unit.children.length > 0) {
        return {
          ...unit,
          children: addNewUnitToSelectedUnit(unit.children, newUnit),
        };
      }
      return unit;
    });
  };

  const editDataStructure = (
    data: OrganizationUnit[],
    objectId: string,
    updatedObject: Partial<OrganizationUnit>,
  ): OrganizationUnit[] => {
    return data.map((unit) => {
      if (unit.id === objectId) {
        return {
          ...unit,
          ...updatedObject,
        };
      }

      if (unit.children && unit.children.length > 0) {
        return {
          ...unit,
          children: editDataStructure(unit.children, objectId, updatedObject),
        };
      }

      return unit;
    });
  };

  const deleteOrganizationUnitById = (
    data: OrganizationUnit[],
    targetId: string,
  ): OrganizationUnit[] => {
    return data.filter((unit) => {
      if (unit.id === targetId) {
        return false;
      } else if (unit.children && unit.children.length > 0) {
        unit.children = deleteOrganizationUnitById(unit.children, targetId);
      }
      return true;
    });
  };

  return (
    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: '8px',
          bgcolor: 'background.paper',
          borderColor: 'divider',
          minHeight: 280,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 300, gap: 1.5 }}>
            <LoadingSpinner label='' />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Loading Organization Hierarchy...
            </Typography>
          </Box>
        ) : organizationTree ? (
          <TreeStructure
            organizationUnit={organizationTree}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onAddClick={onAddUnitClick}
            dragTarget={targetUnit}
          />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              No organization hierarchy found.
            </Typography>
          </Box>
        )}
      </Paper>
      {editedOrgUnits && editedOrgUnits.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderRadius: '8px',
            bgcolor: 'warning.50',
            borderColor: 'warning.200',
          }}
        >
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'warning.dark' }}>
            Changes detected within the tree.
          </Typography>
          <Button
            onClick={applyTreeChanges}
            variant={'primary'}
            label={t(ADMIN_PAGES_KEYS.organizationHierarchy.apply)}
          />
        </Paper>
      )}
      <Modal
          visible={addUnitModal}
          modalContent={<CreateUnitModal addUnit={addNewUnit} />}
          toggle={() => setAddUnitModal(false)}
          headerText={t(ADMIN_PAGES_KEYS.organizationHierarchy.createUnit)}
          headerClass={commonStyles.modalHeader}
          customClass={commonStyles.organizationSettingsModal}
      />
      <Modal
          visible={editNameModal}
          modalContent={
            <EditNameModal
                organizationUnit={selectedOrganizationUnit}
                editName={editName}
            />
          }
          toggle={() => setEditNameModal(false)}
          headerText={t(ADMIN_PAGES_KEYS.organizationHierarchy.editName)}
          headerClass={commonStyles.modalHeader}
          customClass={commonStyles.organizationSettingsModal}
      />
      <Modal
          visible={deleteUnitModal}
          modalContent={
            <DeleteUnitModal
                deleteUnit={deleteUnit}
                cancelEvent={() => setDeleteUnitModal(false)}
            />
          }
          toggleable={false}
          headerText={t(ADMIN_PAGES_KEYS.organizationHierarchy.deleteUnit)}
          headerClass={commonStyles.modalHeader}
          customClass={commonStyles.organizationSettingsModal}
      />
    </Box>
  );
};

export default OrganizationHierarchy;
